import React, { useEffect, useState } from 'react';
import { Button, CloseButton, Flex, Text, useToast } from '@chakra-ui/react';
import { useCookies } from 'react-cookie';
import { FiBell } from 'react-icons/fi';
import { isPushSupported, getPushPermission, subscribeToPush } from '../../services/pushNotifications';
import { colors, radii } from './designSystem';

const DISMISS_KEY = 'pushBannerDismissed';

const PushNotificationBanner = () => {
  const [cookies] = useCookies(['token']);
  const [visible, setVisible] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const toast = useToast();
  const isLoggedIn = Boolean(cookies.token);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (!isPushSupported()) return;

    const permission = getPushPermission();

    // If permission was previously granted, make sure the service worker is
    // registered and the subscription is saved on the server (e.g. after a
    // fresh login, cleared storage, or new device).
    if (permission === 'granted') {
      subscribeToPush().catch((err) => {
        console.error('[PushNotificationBanner] auto-subscribe failed:', err);
      });
      return;
    }

    if (permission !== 'default') return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    setVisible(true);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      if (getPushPermission() === 'granted') {
        setVisible(false);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [visible]);

  const handleEnable = async () => {
    setSubscribing(true);
    const result = await subscribeToPush();
    setSubscribing(false);
    if (result.success || getPushPermission() === 'granted') {
      setVisible(false);
      return;
    }

    setTimeout(() => {
      if (getPushPermission() === 'granted') {
        setVisible(false);
      }
    }, 500);

    if (getPushPermission() !== 'granted') {
      toast({
        title: 'Push notifications blocked',
        description: 'Click the lock icon next to the URL, go to Site settings, set Notifications to "Allow", then refresh the page.',
        status: 'warning',
        duration: 15000,
        isClosable: true,
      });
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      style={{
        backgroundColor: colors.brand[700],
        color: '#fff',
        padding: '12px 16px',
        position: 'relative',
        zIndex: 2000,
        borderBottom: `2px solid ${colors.brand[800]}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <Flex align="center" justify="center" gap={4} maxW="1200px" mx="auto" wrap="wrap">
        <Flex align="center" gap={2}>
          <FiBell size={16} color="#fff" />
          <Text fontSize="sm" fontWeight="600" color="#fff" m={0}>
            Enable push notifications to get order updates even when the app is closed.
          </Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Button
            size="sm"
            bg="#fff"
            color={colors.brand[700]}
            _hover={{ bg: colors.brand[50] }}
            onClick={handleEnable}
            isLoading={subscribing}
            loadingText="Enabling"
            borderRadius={radii.md}
            fontWeight="600"
          >
            Enable
          </Button>
          <CloseButton onClick={handleDismiss} size="sm" color="#fff" />
        </Flex>
      </Flex>
    </div>
  );
};

export default PushNotificationBanner;
